import type { ChangeEvent, ReactNode, Ref } from "react";
import {
  Children,
  isValidElement,
  useCallback,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";

import { CheckIcon } from "@lucide/icons";

import { cx, styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import { Icon } from "simplydo/react/ui/icon";

import type { CheckboxProps } from "./Checkbox.types";
import { CheckboxChildren } from "./CheckboxChildren";
import { CheckboxContent } from "./CheckboxContent";
import { CheckboxContext } from "./CheckboxContext";
import { CheckboxHeader } from "./CheckboxHeader";
import { CheckboxWhenChecked } from "./CheckboxWhenChecked";

type StyleProps = { $trackOn: string };

const SLOT_DISPLAY_NAMES = new Set([
  "CheckboxHeader",
  "CheckboxContent",
  "CheckboxChildren",
  "CheckboxWhenChecked",
]);

const isSlotElement = (child: ReactNode): boolean => {
  if (!isValidElement(child)) {
    return false;
  }
  if (
    child.type === CheckboxHeader ||
    child.type === CheckboxContent ||
    child.type === CheckboxChildren ||
    child.type === CheckboxWhenChecked
  ) {
    return true;
  }
  const type = child.type as { displayName?: string };
  return !!type.displayName && SLOT_DISPLAY_NAMES.has(type.displayName);
};

const hasSlotChildren = (children: ReactNode): boolean => {
  let found = false;
  Children.forEach(children, (child) => {
    if (isSlotElement(child)) {
      found = true;
    }
  });
  return found;
};

const isHeaderElement = (child: ReactNode): boolean => {
  if (!isValidElement(child)) {
    return false;
  }
  if (child.type === CheckboxHeader) {
    return true;
  }
  const type = child.type as { displayName?: string };
  return type.displayName === "CheckboxHeader";
};

const partitionComposedChildren = (children: ReactNode) => {
  const headers: ReactNode[] = [];
  const rest: ReactNode[] = [];
  Children.forEach(children, (child) => {
    if (isHeaderElement(child)) {
      headers.push(child);
    } else if (child != null && child !== false) {
      rest.push(child);
    }
  });
  return { headers, rest };
};

const ComposedRoot = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
  flex: auto;

  &:last-child {
    margin-bottom: 0;
  }

  &.is-disabled {
    cursor: default;
  }
`;

/** Control + Header share this row so they share the same first-line box. */
const TopRow = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
`;

const ControlColumn = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const COLUMN_OFFSET = "12px";
const TOGGLE_WIDTH = "40px";
const TOGGLE_HEIGHT = "20px";
const TOGGLE_HEIGHT_OFFSET = "2px";

const CHECKBOX_SIZE = "17px";

const HeaderColumn = styled.div`
  flex: 1;
  min-width: 0;
  overflow: visible;
`;

const RestColumn = styled.div<{ $toggle?: boolean }>`
  padding-left: ${({ $toggle }) =>
    $toggle
      ? `calc(${COLUMN_OFFSET} + ${TOGGLE_WIDTH})`
      : `calc(${COLUMN_OFFSET} + ${CHECKBOX_SIZE})`};
  padding-right: ${COLUMN_OFFSET};
  padding-top: 0.2rem;
  min-width: 0;
  overflow: visible;
  display: flex;
  flex-direction: column;
`;

const Root = styled.label<StyleProps>`
  position: relative;
  display: inline-block;
  min-height: ${CHECKBOX_SIZE};
  font-size: 1rem;
  line-height: ${CHECKBOX_SIZE};
  min-width: ${CHECKBOX_SIZE};
  backface-visibility: hidden;
  outline: none;
  vertical-align: middle;
  cursor: pointer;
  opacity: 1;

  &.is-disabled {
    cursor: default;
    opacity: 0.45;
  }

  > input {
    position: absolute;
    top: 0;
    left: 0;
    opacity: 0 !important;
    outline: none;
    z-index: -1;
    width: ${CHECKBOX_SIZE};
    height: ${CHECKBOX_SIZE};
  }

  > label {
    display: block;
    padding-left: calc(${CHECKBOX_SIZE} + ${COLUMN_OFFSET});
    font-weight: normal;
    outline: none;
    cursor: inherit;
    color: rgba(0, 0, 0, 0.87);
    transition: color 0.1s ease;
    user-select: none;
  }

  /* Default checkbox / radio box */
  &:not(.is-toggle) > label::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: ${CHECKBOX_SIZE};
    height: ${CHECKBOX_SIZE};
    background: #ffffff;
    border: 1px solid #d4d4d5;
    border-radius: 0.2rem;
    transition:
      border 0.1s ease,
      opacity 0.1s ease,
      transform 0.1s ease,
      box-shadow 0.1s ease;
  }

  &.is-radio:not(.is-toggle) > label::before {
    border-radius: 500rem;
  }

  &.is-radio:not(.is-toggle) > label::after {
    content: "";
    position: absolute;
    top: 4px;
    left: 4px;
    width: 9px;
    height: 9px;
    background: #000000;
    border-radius: 500rem;
    transform: scale(0);
    opacity: 0;
    text-align: center;
    color: rgba(0, 0, 0, 0.95);
  }

  &.is-radio.is-checked:not(.is-toggle) > label::after {
    transform: scale(1);
    opacity: 1;
  }

  /* Toggle */
  &.is-toggle {
    min-height: ${TOGGLE_HEIGHT};
  }

  &.is-toggle > label {
    position: relative;
    padding-left: calc(${COLUMN_OFFSET} + ${TOGGLE_WIDTH});
    min-width: ${TOGGLE_WIDTH};
    min-height: ${TOGGLE_HEIGHT};
    padding-top: 0.15em;
  }

  &.is-toggle > label::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: ${TOGGLE_WIDTH};
    height: ${TOGGLE_HEIGHT};
    background-color: rgba(0, 0, 0, 0.05);
    border-radius: 500rem;
    border: none;
    transition: background 0.3s ease;
  }

  &.is-toggle.is-checked > label::before {
    background-color: ${({ $trackOn }) => $trackOn};
  }

  &.is-toggle > label::after {
    content: "";
    position: absolute;
    top: ${TOGGLE_HEIGHT_OFFSET};
    left: ${TOGGLE_HEIGHT_OFFSET};
    width: calc(${TOGGLE_HEIGHT} - ${TOGGLE_HEIGHT_OFFSET} * 2);
    height: calc(${TOGGLE_HEIGHT} - ${TOGGLE_HEIGHT_OFFSET} * 2);
    background: #ffffff linear-gradient(transparent, rgba(0, 0, 0, 0.05));
    border-radius: 500rem;
    box-shadow:
      0 1px 2px 0 rgba(34, 36, 38, 0.15),
      0 0 0 1px rgba(34, 36, 38, 0.15) inset;
    transition: left 0.3s ease;
  }

  &.is-toggle.is-checked > label::after {
    left: calc(${TOGGLE_WIDTH} - ${TOGGLE_HEIGHT} + ${TOGGLE_HEIGHT_OFFSET});
  }
`;

const CheckMark = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  width: ${CHECKBOX_SIZE};
  height: ${CHECKBOX_SIZE};
  display: none;
  pointer-events: none;
  align-items: center;
  justify-content: center;

  &.is-visible {
    display: flex;
  }
`;

type ControlProps = {
  inputRef: Ref<HTMLInputElement>;
  inputId: string;
  checked: boolean;
  trackOn: string;
  className?: string;
  labelContent?: ReactNode;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
} & Pick<
  CheckboxProps,
  | "toggle"
  | "type"
  | "disabled"
  | "name"
  | "value"
  | "readOnly"
  | "tabIndex"
  | "style"
>;

const Control = ({
  inputRef,
  inputId,
  checked,
  trackOn,
  className,
  labelContent,
  toggle,
  type = "checkbox",
  disabled,
  name,
  value,
  readOnly,
  tabIndex,
  style,
  onChange,
}: ControlProps) => {
  const isRadio = type === "radio";
  return (
    <Root
      className={cx(
        toggle && "is-toggle",
        isRadio && "is-radio",
        disabled && "is-disabled",
        checked && "is-checked",
        className,
      )}
      style={style}
      htmlFor={inputId}
      $trackOn={trackOn}
    >
      <input
        ref={inputRef}
        id={inputId}
        type={type}
        name={name}
        value={value === undefined ? undefined : String(value)}
        checked={checked}
        disabled={disabled}
        readOnly={readOnly}
        tabIndex={tabIndex}
        onChange={onChange}
      />
      <label htmlFor={inputId}>{labelContent}</label>
      {!toggle && !isRadio ? (
        <CheckMark className={cx(checked && "is-visible")}>
          <Icon icon={CheckIcon} size="small" aria-hidden />
        </CheckMark>
      ) : null}
    </Root>
  );
};

const CheckboxComponent = (props: CheckboxProps) => {
  const {
    checked: checkedProp,
    defaultChecked,
    onChange,
    onClick,
    children,
    toggle,
    type = "checkbox",
    disabled,
    name,
    value,
    id,
    className,
    style,
    readOnly,
    tabIndex,
  } = props;

  const theme = useTheme();
  const autoId = useId();
  const inputId = id || autoId;
  const inputRef = useRef<HTMLInputElement>(null);
  const [uncontrolled, setUncontrolled] = useState(!!defaultChecked);
  const isControlled = checkedProp !== undefined;
  const checked = isControlled ? !!checkedProp : uncontrolled;
  const composed = hasSlotChildren(children);
  const isRadio = type === "radio";
  const trackOn = theme?.primaryColour || "#2185d0";

  const emit = (event: ChangeEvent<HTMLInputElement>, next: boolean) => {
    const data = { ...props, checked: next };
    onClick?.(event, data);
    onChange?.(event, data);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (disabled || readOnly) {
      return;
    }
    const next = e.target.checked;
    if (!isControlled) {
      setUncontrolled(next);
    }
    emit(e, next);
  };

  const toggleChecked = useCallback(() => {
    if (disabled || readOnly) {
      return;
    }
    if (isRadio && checked) {
      return;
    }
    inputRef.current?.click();
  }, [checked, disabled, isRadio, readOnly]);

  const contextValue = useMemo(
    () => ({ checked, disabled: !!disabled, toggle: toggleChecked }),
    [checked, disabled, toggleChecked],
  );

  if (composed) {
    const { headers, rest } = partitionComposedChildren(children);
    return (
      <CheckboxContext.Provider value={contextValue}>
        <ComposedRoot
          className={cx(disabled && "is-disabled", className)}
          style={style}
        >
          <TopRow>
            <ControlColumn>
              <Control
                inputRef={inputRef}
                inputId={inputId}
                checked={checked}
                trackOn={trackOn}
                toggle={toggle}
                type={type}
                disabled={disabled}
                name={name}
                value={value}
                readOnly={readOnly}
                tabIndex={tabIndex}
                onChange={handleChange}
              />
            </ControlColumn>
            {headers.length > 0 ? <HeaderColumn>{headers}</HeaderColumn> : null}
          </TopRow>
          {rest.length > 0 ? (
            <RestColumn $toggle={toggle}>{rest}</RestColumn>
          ) : null}
        </ComposedRoot>
      </CheckboxContext.Provider>
    );
  }

  return (
    <Control
      inputRef={inputRef}
      inputId={inputId}
      checked={checked}
      trackOn={trackOn}
      className={className}
      style={style}
      labelContent={children}
      toggle={toggle}
      type={type}
      disabled={disabled}
      name={name}
      value={value}
      readOnly={readOnly}
      tabIndex={tabIndex}
      onChange={handleChange}
    />
  );
};

export const Checkbox = styled(CheckboxComponent)``;
